import { useState, useEffect, ChangeEvent, FormEvent, useImperativeHandle, forwardRef, useMemo } from "react";
import {
  Box,
  Button,
  Typography,
} from "@mui/material";
import { RobotFormField } from "./RobotFormField";
import { getTranslation } from "common";
import { useTranslations } from "hooks";

interface RobotValuesEditorProps {
  schema: any;
  initialData?: any;
  onSave?: (data: any) => Promise<void>;
  showSaveButton?: boolean;
  onValidationChange?: (isValid: boolean) => void;
  onDataChange?: (data: any) => void;
  onUserDataTrigger?: (
    fieldName: string,
    anchorEl: HTMLElement
  ) => void;
  userdataschema?: any[];
  hideSectionStyling?: boolean;
}

export interface RobotValuesEditorRef {
  getFormData: () => any;
  isValid: () => boolean;
  setFormData: (data: any) => void;
}

export const RobotFormRender = forwardRef<RobotValuesEditorRef, RobotValuesEditorProps>(({
  schema,
  initialData = {},
  onSave,
  showSaveButton = true,
  onValidationChange,
  onDataChange,
  onUserDataTrigger,
  userdataschema,
  hideSectionStyling = true,
}, ref) => {
  const translate = useTranslations()
  const initializedSchema = useMemo(() => {
    if (!schema) return null;

    const processed = JSON.parse(JSON.stringify(schema));
    Object.values(processed).forEach((section: any) => {
      if (section && section.properties) {
        Object.entries(section.properties).forEach(([key, def]: any) => {
          if (def.value === undefined) {
            if (initialData && initialData[key] !== undefined) {
              def.value = initialData[key];
            } else {
              const effectiveType = def.format === "textarea" ? "textarea" : def.type;

              switch (effectiveType) {
                case "string":
                case "textarea":
                case "password":
                  def.value = "";
                  break;
                case "number":
                  def.value = "";
                  break;
                case "boolean":
                  def.value = false;
                  break;
                default:
                  def.value = "";
              }
            }
          }
        });
      }
    });

    return processed;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schema, JSON.stringify(initialData)]);

  const [workingSchema, setWorkingSchema] = useState<any>(initializedSchema);

  useEffect(() => {
    setWorkingSchema(initializedSchema);
  }, [initializedSchema]);

  useEffect(() => {
    if (!workingSchema) return;

    const isFormValid = validateForm();
    onValidationChange?.(isFormValid);

    const values: any = {};
    Object.values(workingSchema).forEach((section: any) => {
      if (section?.properties) {
        Object.entries(section.properties).forEach(([key, def]: any) => {
          values[key] = def.value;
        });
      }
    });
    onDataChange?.(values);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workingSchema, onValidationChange, onDataChange]);

  const validateForm = (): boolean => {
    if (!workingSchema) return true;

    for (const section of Object.values(workingSchema) as any) {
      if (section && section.properties) {
        for (const key in section.properties) {
          const def = section.properties[key];
          if (def.required) {
            const value = def.value;
            const effectiveType = def.format === "textarea" ? "textarea" : def.type;
            if ((effectiveType === "string" || effectiveType === "textarea" || effectiveType === "password") && (!value || value.trim() === "")) {
              return false;
            }
            if (effectiveType === "number" && (value === "" || value === null || value === undefined)) {
              return false;
            }
            if (effectiveType === "boolean" && value === undefined) {
              return false;
            }
          }
        }
      }
    }
    return true;
  };

  useImperativeHandle(ref, () => ({
    getFormData: () => {
      const trimmedSchema = JSON.parse(JSON.stringify(workingSchema));
      Object.values(trimmedSchema).forEach((section: any) => {
        if (section?.properties) {
          Object.entries(section.properties).forEach(([key, def]: any) => {
            const effectiveType = def.format === "textarea" ? "textarea" : def.type;
            if ((effectiveType === "string" || effectiveType === "textarea") && typeof def.value === "string") {
              def.value = def.value.trim();
            }
          });
        }
      });
      return trimmedSchema;
    },
    isValid: validateForm,
    setFormData: (data: any) => {
      setWorkingSchema((prev: any) => {
        if (!prev) return prev;

        const updated = JSON.parse(JSON.stringify(prev));
        Object.values(updated).forEach((section: any) => {
          if (section?.properties) {
            Object.keys(section.properties).forEach(key => {
              if (data[key] !== undefined) {
                section.properties[key].value = data[key];
              }
            });
          }
        });
        return updated;
      });
    },
  }));

  if (!workingSchema) {
    return <Typography>{getTranslation(translate, "robot.schema_not_available")}</Typography>;
  }

  const handleChange = (key: string, value: any) => {
    setWorkingSchema((prev: any) => {
      const updated = JSON.parse(JSON.stringify(prev));

      for (const sectionKey in updated) {
        const section = updated[sectionKey];
        if (section?.properties && section.properties[key]) {
          section.properties[key].value = value;
          break;
        }
      }
      return updated;
    });
  };

  const handleFileChange = (key: string, e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result as string;
        handleChange(key, base64);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (onSave) {
      await onSave(workingSchema);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      {Object.entries(workingSchema).map(([sectionKey, section]: [string, any]) => {
        if (!section || !section.properties) return null;

        return (
          <Box key={sectionKey} mb={2} component="fieldset" sx={hideSectionStyling ? { border: 'none', p: 0 } : { border: '1px solid #ccc', borderRadius: '4px', p: 2 }}>
            {!hideSectionStyling && <Typography component="legend" variant="h6" sx={{ px: 1 }}>{sectionKey}</Typography>}
            {Object.entries(section.properties).map(([key, def]: any) => {
              const isRequired = def.required === true;
              const value = def.value;

              return (
                <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 2, my: 1 }}>
                  <Box sx={{ flexGrow: 1 }}>
                    <RobotFormField
                      fieldKey={key}
                      def={def}
                      value={value}
                      isRequired={isRequired}
                      handleChange={handleChange}
                      handleFileChange={handleFileChange}
                      onUserDataTrigger={onUserDataTrigger}
                      userdataschema={userdataschema}
                    />
                  </Box>
                </Box>
              )
            })}
          </Box>
        );
      })}

      {showSaveButton && (
        <Box mt={2}>
          <Button type="submit" variant="contained">
            Salva
          </Button>
        </Box>
      )}
    </Box>
  );
});